import { useSyncExternalStore, type ReactElement } from "react";
import { CURRENCY_COIN } from "odyssey-shared";
import { barFill } from "../../ui/kit/bar.ts";
import { logical } from "../../ui/kit/metrics.ts";
import type { TipController } from "../bar/tips.ts";
import { useTipHandlers } from "../bar/tip-handlers.ts";
import { WALLET_LINE, WalletLine } from "../wallet/line.tsx";
import type { WalletSource } from "../wallet/store.ts";
import { CARRY, CARRY_TIP_ANCHOR, carryFull, carryName, carryOf, carryText, footerFit, type Carry } from "./carry.ts";
import type { BagSnapshot } from "./snapshot.ts";

/**
 * The Inventory window's footer (P2.8-T53, ADR-125 decision 4.3 and its note for P2.8-T53; ADR-124 decision 6.1): the
 * carry number at the left, the wallet line at the right, at least `CARRY.lineGap` apart, narrowed in stages where
 * the window is too narrow for both (`footerFit`).
 */

/**
 * Nothing to hear, for a footer with no wallet store.
 *
 * @returns A no-op unsubscribe.
 */
const NO_SUBSCRIBE = (): (() => void) => () => undefined;

/**
 * No wallet, for a footer with no wallet store.
 *
 * @returns Null.
 */
const NO_WALLET = (): null => null;

/**
 * The carry number: the bar of slots used, cropped to whole logical pixels, then "used/size", muted or in the warning
 * colour when the bag is full; one named picture and one tooltip anchor, focusable where the page shows tooltips, as
 * tall as a wallet mark so both stand on whole logical pixels and their tooltips at the same height.
 *
 * @param props The `carry`, whether the bar is drawn (`bar`), and whether it takes keyboard focus (`focusable`).
 * @returns The carry number.
 */
function CarryNumber({ carry, bar, focusable }: { readonly carry: Carry; readonly bar: boolean; readonly focusable: boolean }): ReactElement {
  const full = carryFull(carry);
  const fill = barFill(carry.used, carry.size, CARRY.bar - 2 * CARRY.barEdge);
  return (
    <span className={full ? "ot-bag-carry is-full" : "ot-bag-carry"} data-bag="carry" data-full={full ? "" : undefined} data-tip={CARRY_TIP_ANCHOR} role="img" aria-label={carryName(carry)} tabIndex={focusable ? 0 : undefined} style={{ columnGap: logical(CARRY.gap), height: logical(WALLET_LINE.box) }}>
      {bar ? (
        <span className="ot-hud-bar ot-bag-carry__bar" data-bag="carry-bar" style={{ width: logical(CARRY.bar), height: logical(CARRY.barHeight) }}>
          <span className="ot-hud-bar__fill" data-fill={fill} style={{ width: logical(fill) }} />
        </span>
      ) : null}
      <span className="ot-bag-carry__count" data-bag="carry-count">
        {carryText(carry)}
      </span>
    </span>
  );
}

/**
 * The footer's content.
 *
 * @param props The `bag` drawn, the `wallet` store (null for no wallet line), the window's width in logical pixels
 *   (`width`), and the page's tooltip controller (`tips`, null where nothing shows tooltips).
 * @returns The footer's row.
 * @example
 * <BagFooter bag={snapshot} wallet={wallet} width={300} tips={tips} />
 */
export function BagFooter({ bag, wallet, width, tips }: { readonly bag: BagSnapshot; readonly wallet: WalletSource | null; readonly width: number; readonly tips: TipController | null }): ReactElement {
  const purse = useSyncExternalStore(wallet?.subscribe ?? NO_SUBSCRIBE, wallet?.getSnapshot ?? NO_WALLET, wallet?.getSnapshot ?? NO_WALLET);
  const handlers = useTipHandlers(tips);
  const carry = carryOf(bag);
  const copper = purse?.[CURRENCY_COIN] ?? null;
  const fit = footerFit(width, carry === null ? null : carryText(carry), copper);
  return (
    <span className="ot-bag-footer" data-bag="footer" style={{ columnGap: logical(CARRY.lineGap), minHeight: logical(WALLET_LINE.box) }}>
      {carry === null || !fit.carry ? null : (
        <span className="ot-bag-footer__carry" {...handlers}>
          <CarryNumber carry={carry} bar={fit.bar} focusable={tips !== null} />
        </span>
      )}
      {wallet === null ? null : <WalletLine source={wallet} tips={tips} bare={fit.bare} />}
    </span>
  );
}
